import React from "react"
import {Tooltip as ReactTooltip} from "react-tooltip"

import moment from "moment"

import {
    ActivityCategory,
    Document,
    DocumentFileFormat,
    DocumentType,
    GedFieldDataType,
    MetadataConfig,
    Nature,
    PostalAddress,
    TenantUser,
    UserProfile
} from "src/_generated/typed-document-nodes"
import {ArchiveIcon, MedalIcon, PostIcon} from "src/components/common/NdIcons/NdIcons"
import {PostalMediaType} from "src/components/common/NdIcons/SvgCollection"
import {
    ArchiveActivityIcon,
    AttachmentAddedActivityIcon,
    CanceledActivityIcon,
    ChangeChorusStatusActivityIcon,
    CompletedActivityIcon,
    CreateActivityIcon,
    DeletedActivityIcon,
    ErrorActivityIcon,
    ExternalProcessActivityIcon,
    LaunchActivityIcon,
    MetaDataChangeActivityIcon,
    ReadActivityIcon,
    RoutingActivityIcon,
    SendActivityIcon,
    SendToChorusActivityIcon
} from "src/components/common/NdIcons/SvgCollectionActivity"
import {ColumnItem} from "src/components/Document/DocumentTable"
import {CHORUS_NATURES, DOCUMENTS_TO_NOT_REQUEST, NATURE_CYCLE, NatureCycleUnion} from "src/constants/document"
import {localStorageItemName} from "src/constants/local-storage-item-name"

import {BusinessConfigType} from "./@types/common"
import {Service} from "./@types/ServiceInterface"
import {KeyValue, Tenant} from "./@types/TenantInterface"
import {countries} from "./countries"
import {getTenantName} from "./fn/auth"
import {RoleAuth} from "./types/HomeInterface"
import {Environment} from "./types/RolesInterface"

const DOMAIN_ADMINISTRATOR = "DOMAIN_ADMINISTRATOR"
export const BUSINESS_API_URL = "BUSINESS_API_URL"
export const odmToken = "odmToken"

// Reuse Intl.NumberFormat instances per culture
const numberFormatCache = new Map<string, Intl.NumberFormat>()

const CommonUtils = {
    defaultNumeriaId: "DOCOONPRN",
    GetSetting: function (name: string) {
        const appSettingsString = localStorage.getItem(localStorageItemName.appSettings)
        if (appSettingsString !== null) {
            const settings = JSON.parse(appSettingsString)
            return settings[name]
        }
        return null
    },
    isDocumentConform: function (document: Document): boolean {
        // Récupérer les listes depuis le localStorage
        const listDocumentType: DocumentType[] = JSON.parse(
            localStorage.getItem(localStorageItemName.listDocumentType) || "[]"
        )
        const listNatures: Nature[] = JSON.parse(localStorage.getItem(localStorageItemName.listNatures) || "[]")
        // Récupérer le type de document
        const documentType = listDocumentType.find((type) => type.code === document.documentTypeCode)

        // Récupérer la nature
        const nature = listNatures.find((nat) => nat.code?.toUpperCase() === documentType.natureCode?.toUpperCase())

        // Récupérer les metadataConfigs combinés
        const metadataConfigs: MetadataConfig[] = [
            ...(documentType?.metadataConfigs || []),
            ...(nature?.metadataConfigs || [])
        ]

        // Filtrer les métadonnées obligatoires
        const mandatoryConfigs = metadataConfigs.filter((config) => config.isMandatory)

        // Vérifier si toutes les métadonnées obligatoires sont présentes dans les gedFields
        const gedFieldsKeys = new Set(document.gedFields?.map((field) => field.key))

        const gedFieldMap = new Map(document.gedFields?.map((gedField) => [gedField.key, gedField.value]))

        if (["O2CEinvoice", "P2PEinvoice"].includes(nature?.code)) {
            if (gedFieldMap.has("Internal_InvoiceFormat")) {
                if (gedFieldMap.get("Invoice_InvoiceFormat") !== DocumentFileFormat.Custom) {
                    return !!gedFieldMap.get("Internal_Valid")
                }
            }
        }

        return (
            mandatoryConfigs.every((config) => gedFieldsKeys.has(config.metadataCode)) &&
            ["O2CEinvoice", "P2PEinvoice"].includes(nature?.code)
        )
    },
    categoryIcons: (props: React.SVGProps<SVGSVGElement>): Partial<Record<ActivityCategory, JSX.Element>> => {
        // fixme code pas terrible, à reprendre
        return {
            [ActivityCategory.ChorusB_2GCompleted]: <CompletedActivityIcon {...props} />,
            [ActivityCategory.ChorusB_2GSent]: <SendToChorusActivityIcon {...props} />,
            [ActivityCategory.ChorusB_2GStatusChanged]: <ChangeChorusStatusActivityIcon {...props} />,
            [ActivityCategory.DocumentArchived]: <ArchiveActivityIcon {...props} />,
            [ActivityCategory.DocumentAttachmentsAdded]: <AttachmentAddedActivityIcon {...props} />,
            [ActivityCategory.DocumentCanceled]: <CanceledActivityIcon {...props} />,
            [ActivityCategory.DocumentMetadataEdited]: <MetaDataChangeActivityIcon {...props} />,
            [ActivityCategory.DocumentRead]: <ReadActivityIcon {...props} />,
            [ActivityCategory.DocumentRemoved]: <DeletedActivityIcon color={"rgba(218, 79, 163, 1)"} {...props} />,
            [ActivityCategory.DocumentSigned]: <MedalIcon {...props} />,
            [ActivityCategory.Error]: <ErrorActivityIcon color={"rgba(218, 79, 163, 1)"} {...props} />,
            [ActivityCategory.ExternalProcess]: <ExternalProcessActivityIcon {...props} />,
            [ActivityCategory.JobCompleted]: <CompletedActivityIcon {...props} />,
            [ActivityCategory.JobCreated]: <CreateActivityIcon {...props} />,
            [ActivityCategory.JobLaunched]: <LaunchActivityIcon {...props} />,
            [ActivityCategory.JobMessageCanceled]: <CanceledActivityIcon color={"rgba(218, 79, 163, 1)"} {...props} />,
            [ActivityCategory.JobMessageRelaunched]: <SendActivityIcon {...props} />,
            [ActivityCategory.JobPreProcessed]: <LaunchActivityIcon {...props} />,
            [ActivityCategory.JobProcessing]: <SendActivityIcon {...props} />,
            [ActivityCategory.NotificationSent]: <SendActivityIcon {...props} />,
            [ActivityCategory.NumeriaPostalSent]: (
                <PostIcon {...props} className={`${props.className || ""} text-primary bg-icon-primary-opacity`} />
            ),
            [ActivityCategory.NumeriaPostalStatusChanged]: <PostalMediaType {...props} />,
            [ActivityCategory.Routing]: <RoutingActivityIcon {...props} />,
            [ActivityCategory.DocumentSigningSkipped]: (
                <MedalIcon {...props} className={`${props.className || ""} text-secondary bg-icon-secondary-opacity`} />
            ),
            [ActivityCategory.DocumentWaitingArchive]: <ArchiveIcon {...props} />,
            [ActivityCategory.DocumentRecipientAdded]: <CreateActivityIcon {...props} />
        }
    },
    isExternal(userName: string) {
        // Vérifie si le userName est une adresse email
        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userName)

        // Vérifie si le userName ne contient que des chiffres
        const isOnlyDigits = /^\d+$/.test(userName)

        // Vérifie si le userName commence par "GEST"
        const startsWithGEST = userName.startsWith("GEST")

        // Retourne true si l'une des conditions est remplie
        return isEmail || isOnlyDigits || startsWithGEST
    },
    firstLetterUpperCase(valCap: string) {
        const val = valCap.toLocaleLowerCase()
        return String(val).charAt(0).toUpperCase() + String(val).slice(1)
    },

    IsCurrentUserAdmin() {
        const profile = JSON.parse(localStorage.getItem(localStorageItemName.selectedRole)).profile
        return (
            profile === UserProfile.DomainAdministrator ||
            profile === UserProfile.Administrator ||
            profile === UserProfile.EnvironmentAdministrator ||
            profile === UserProfile.WebsiteAdministrator
        )
    },

    IsCurrentUserStandardUser() {
        const profile = JSON.parse(localStorage.getItem(localStorageItemName.selectedRole)).profile
        return profile === UserProfile.StandardUser || profile === UserProfile.TenantReceiver
    },

    CheckValidIp: (ipaddress: string) => {
        return /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
            ipaddress
        )
    },
    GetSettingInit: () => {
        fetch(window.location.origin + "/settings.json", {
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json"
            }
        })
            .then(function (response) {
                return response.json()
            })
            .then(function (myJson) {
                localStorage.setItem("appSettings", JSON.stringify(myJson))
            })
    },
    getFilterLabel: (FilterItem, propsfilterToApply) => {
        const filterToApply = [...propsfilterToApply]
        const filterIndex = filterToApply.findIndex((item) => {
            if (item.field === "METADATA") {
                return item.filterArrayElementName === FilterItem.filterOption.filterField
            } else {
                return item.field === FilterItem.filterOption.filterField
            }
        })

        if (filterIndex > -1) {
            const filterValue = filterToApply[filterIndex].value
            const filterType = typeof filterValue

            switch (filterType) {
                case "number":
                    switch (filterToApply[filterIndex].operator) {
                        case "BETWEEN":
                            return `Entre ${filterValue} et ${filterToApply[filterIndex].value2}`
                        case "GREATER_THAN_OR_EQUAL":
                            return `Plus grand que ${filterValue}`
                        case "LOWER_THAN_OR_EQUAL":
                            return `Plus petit que ${filterValue}`
                        default:
                            return FilterItem.title
                    }
                default:
                    switch (filterToApply[filterIndex].filterType) {
                        // FIXME vraie enum à mettre + gestion insensitive
                        case "statusFilters":
                        case "subStatusFilters":
                        case "transmissionModeFilters":
                            const item = FilterItem.filterOption.listItem?.find((item) => item.value === filterValue)
                            return item ? item.key : filterValue
                        case "stringFilters":
                            return String(filterValue)
                                .split(",")
                                .map(
                                    (value) =>
                                        FilterItem.filterOption.listItem?.find(
                                            (i: {value: string; key: string}) => i.value === value
                                        )?.key ?? value
                                )
                                .join(", ")
                        case "dateFilters":
                            const formattedValue = moment(filterValue).format("DD/MM/YYYY")
                            const formattedValue2 = moment(filterToApply[filterIndex].value2).format("DD/MM/YYYY")
                            switch (filterToApply[filterIndex].operator) {
                                case "BETWEEN":
                                    return `Du ${formattedValue} au ${formattedValue2}`
                                case "GREATER_THAN_OR_EQUAL":
                                    return `Après le ${formattedValue}`
                                case "LOWER_THAN_OR_EQUAL":
                                    return `Avant le ${formattedValue}`
                                default:
                                    return FilterItem.title
                            }
                        default:
                            return filterValue
                    }
            }
        } else {
            return FilterItem.title
        }
    },
    getMinWith: (space: number, MINCOLWITH: number, copyColumn: ColumnItem[], index: number): number => {
        let width: number = MINCOLWITH
        const visibleColumns = copyColumn.filter((item) => !item.isHidden)
        const initialColWidth = visibleColumns[index].width
        const widthTotal = space - 220

        // Calculer la largeur totale occupée par les colonnes avec des tailles en pourcentage
        const totalInitialWidth = visibleColumns.reduce((sum, item) => {
            sum += item.width * (widthTotal / 100)
            return sum
        }, 0)
        const totalInitialWidthPercentage = visibleColumns.reduce((sum, item) => {
            sum += item.width
            return sum
        }, 0)

        // Si la largeur totale est déjà occupée, on conserve les tailles définies en pourcentage
        if (totalInitialWidthPercentage >= 100) {
            width = initialColWidth * (widthTotal / 100)
        } else {
            // Sinon, on peut redimensionner les colonnes
            if ((initialColWidth * widthTotal) / 100 < MINCOLWITH) {
                width = MINCOLWITH
            } else {
                width = initialColWidth * (widthTotal / 100)

                // Si de l'espace est disponible, la première colonne prend plus de largeur
                if (index === 0) {
                    width = widthTotal - totalInitialWidth + width // Ajout d'espace disponible à la première colonne
                }
            }
        }
        return width
    },

    GetNatureList: function (cycle: string) {
        const listNature = localStorage.getItem(localStorageItemName.listNatures)
        if (listNature !== null) {
            if (cycle !== "") {
                return JSON.parse(listNature)?.filter((item: Nature) => {
                    return item.cycle === cycle
                })
            } else {
                return JSON.parse(listNature)
            }
        }
        return []
    },
    /** Get a nature (with its metadataConfigs) from the localStorage by its code. */
    GetNatureByCode: function (code: string): Nature | null {
        if (!code) {
            return null
        }

        const listNature = localStorage.getItem(localStorageItemName.listNatures)
        if (!listNature) {
            return null
        }

        try {
            const natures: Nature[] = JSON.parse(listNature)
            const nature: Nature | undefined = natures.find(
                (nature) => nature.code?.toUpperCase() === code.toUpperCase()
            )

            return nature ?? null
        } catch (error) {
            console.error("Error parsing natures from localStorage:", error)
            return null
        }
    },
    GetNatureListStartsByCode: function (cycle: NatureCycleUnion, oldTab: number) {
        const cycleEscaped = cycle?.replaceAll("_", "")?.toUpperCase()
        // Get all Natures with code starting by cycle
        const listNature = localStorage.getItem(localStorageItemName.listNatures)
        const naturesFilteredByCycle = listNature
            ? (JSON.parse(listNature)
                  ?.filter((item: Nature) => {
                      return item.code?.toUpperCase()?.startsWith(cycleEscaped) && NATURE_CYCLE[item.cycle]
                  })
                  .filter(
                      (item: Nature) =>
                          !DOCUMENTS_TO_NOT_REQUEST.map((d) => d.toUpperCase()).includes(item.code?.toUpperCase())
                  )
                  ?.filter(Boolean) ?? [])
            : []

        // filter on index with oldTab && handle out of range before crashing for customViews
        if (oldTab > 0 && oldTab - 1 < naturesFilteredByCycle.length) {
            return [naturesFilteredByCycle?.[oldTab - 1]]
        }
        const customViewIndex = oldTab - 1 - naturesFilteredByCycle.length
        const customView = CommonUtils.GetAllColView(cycle)?.[customViewIndex]
        if (customView) {
            const natureCodesList =
                customView?.natureCodes
                    ?.split(",")
                    ?.filter((nt) => !CHORUS_NATURES.map((c) => c.toUpperCase()).includes(nt?.toUpperCase())) ?? []
            return (
                naturesFilteredByCycle?.filter((item: Nature) => {
                    return (
                        natureCodesList.some((code) => code?.toUpperCase() === item?.code?.toUpperCase()) &&
                        item?.cycle === cycle
                    )
                }) ?? []
            )
        }
        return naturesFilteredByCycle
    },
    GetAllNaturesByCycle: function (cycle: NatureCycleUnion): Array<Nature> {
        const listNature = localStorage.getItem(localStorageItemName.listNatures)
        return listNature
            ? (JSON.parse(listNature)
                  ?.filter((item: any) => {
                      return (
                          item.cycle === cycle &&
                          item?.code?.toUpperCase()?.startsWith(NATURE_CYCLE[cycle]?.replaceAll("_", ""))
                      )
                  })
                  ?.filter(Boolean) ?? [])
            : []
    },
    /**
     * Computes the index of the tab to redirect to after creating/editing a
     * custom view. Tab order is: [overview?, natures..., favorites...].
     *
     * @param {Array<{ name: string; table: string }>} customViews - Full list of views AFTER insertion/update.
     * @param {string} tableName - Name of the current table / cycle.
     * @param {string | undefined} natureCodes - Associated natures.
     * @param {string} oldName - Existing target name.
     * @param {number} insertedIndex - Position of the view in customViews (used for simple tables).
     * @returns {number} The index of the tab to redirect to.
     */
    GetNewViewTabIndex: function (
        customViews: Array<{name: string; table: string}>,
        tableName: string,
        natureCodes: string | undefined,
        oldName: string,
        insertedIndex: number
    ): number {
        if (!natureCodes) {
            return insertedIndex
        }

        const naturesByCycle = this.GetAllNaturesByCycle(tableName as NatureCycleUnion) || []
        const naturesLength: number = naturesByCycle.filter(
            (nature: Nature) =>
                !DOCUMENTS_TO_NOT_REQUEST.map((d) => d.toUpperCase()).includes(nature.code?.toUpperCase())
        ).length

        // The "overview" tab is only added when there is more than one nature.
        const natureOffset = naturesLength === 1 ? 0 : naturesLength
        const customViewsFilteredByTable = customViews.filter((item) => item.table === tableName)
        const natureFound = customViews.some((item) => item.name === oldName && item.table === tableName)

        // Creation : the new view is the last of the favorites for this table.
        if (!natureFound) {
            return (customViewsFilteredByTable?.length || 0) + natureOffset
        }

        // Edit : we land back on the position of the edited favorite.
        const indexFoundOnFilteredCustomViews =
            customViewsFilteredByTable.findIndex((item) => item.name === oldName) + 1
        return indexFoundOnFilteredCustomViews + natureOffset
    },
    GetAllNatures: function (): any[] {
        const listNature = localStorage.getItem(localStorageItemName.listNatures)
        if (listNature !== null) {
            return JSON.parse(listNature)
        }
        return []
    },
    GetAllDocumentTypes: function (): any[] {
        const listDocumentType = localStorage.getItem(localStorageItemName.listDocumentType)
        if (listDocumentType !== null) {
            return JSON.parse(listDocumentType)
        }
        return []
    },
    GetNatureFromDocumentTypeCode: function (code: string): Nature | null {
        const documentType = CommonUtils.GetAllDocumentTypes().find((dt) => dt.code === code)
        if (!documentType) return null

        return CommonUtils.GetAllNatures().find((nt) => nt.code === documentType.natureCode) ?? null
    },
    FormatPostalAddress: function (postalAddress: PostalAddress): string {
        return (
            (postalAddress?.additionalInfo ? postalAddress?.additionalInfo : "") +
            (postalAddress?.street ? postalAddress?.street : "") +
            " " +
            (postalAddress?.postBox ? postalAddress?.postBox : "") +
            " " +
            (postalAddress?.zipCode ? postalAddress?.zipCode : "") +
            " " +
            (postalAddress?.city ? postalAddress?.city : "") +
            " " +
            (postalAddress?.country ? postalAddress?.country : "")
        )
    },
    renderDataTimeField(attributions, value, read, readable, widthValue, withHours = true, withToolTip = false) {
        const dateFormat = CommonUtils.equalsIgnoreCase(CommonUtils.GetLocal(localStorageItemName.language), "FR")
            ? "DD/MM/YYYY"
            : "MM/DD/YYYY"

        // Vérifie si la valeur est vide
        if (!value) {
            return <span>&nbsp;</span>
        }

        // Formatage de la date
        const formattedDate = moment(value)
            .locale(CommonUtils.GetLocal(localStorageItemName.language))
            .format(dateFormat)

        // Formatage de l'heure si nécessaire
        const formattedTime = withHours ? (
            <>
                {" "}
                <br />
                <span style={{fontSize: 9}}>
                    {moment(value).locale(CommonUtils.GetLocal(localStorageItemName.language)).format("HH:mm")}
                </span>
            </>
        ) : null

        // Construction du texte complet avec date et heure
        const tooltipText = `${moment(value)
            .locale(CommonUtils.GetLocal(localStorageItemName.language))
            .format("dddd DD MMMM YYYY")} ${attributions} ${moment(value)
            .locale(CommonUtils.GetLocal(localStorageItemName.language))
            .format("HH:mm")}`
        return (
            <span
                style={{
                    fontFamily: '"Poppins Light", serif !important',
                    fontWeight: "400",
                    width: widthValue
                }}
                className={readable && !read ? "noRead" : ""}
                data-for={withToolTip ? "dateShort" : null}
                data-tip={tooltipText}
            >
                {formattedDate}
                {formattedTime}
                {withToolTip && <ReactTooltip id="dateShort" place={"bottom"} />}
            </span>
        )
    },
    getTotalWidth(colList, widthInit) {
        let total = 0
        colList.forEach((item) => {
            if (typeof item.width === "string") {
                total = total + 180
            }
            if (typeof item.width === "number") {
                total = total + (item.width * widthInit) / 100
            }
        })

        return total
    },
    getPostalInformation: function (
        jobpostalAddress: PostalAddress | undefined,
        ServicepostalAddress: PostalAddress | undefined,
        TenantpostalAddress: PostalAddress | undefined
    ): string {
        return jobpostalAddress
            ? this.FormatPostalAddress(jobpostalAddress)
            : ServicepostalAddress
              ? this.FormatPostalAddress(ServicepostalAddress)
              : TenantpostalAddress
                ? this.FormatPostalAddress(TenantpostalAddress)
                : ""
    },
    getChorusIndo: function (service: Service | undefined, tenant: Tenant | undefined): string {
        return service?.chorusB2GConfig
            ? service.chorusB2GConfig?.login
            : tenant?.chorusB2GConfig
              ? tenant?.chorusB2GConfig?.login
              : ""
    },
    getChorusName: function (service: Service | undefined, tenant: Tenant | undefined): string {
        return service?.name ? service.name : tenant?.name ? tenant.name : ""
    },
    getPostalName: function (
        jobParameter: PostalAddress | undefined,
        service: PostalAddress | undefined,
        tenant: Tenant | undefined
    ): string {
        return jobParameter?.name ? jobParameter.name : service?.name ? service.name : tenant?.name ? tenant.name : ""
    },
    GetAllDocTypes: function (): any[] {
        const listDocTypes = localStorage.getItem(localStorageItemName.listDocumentType)
        if (listDocTypes !== null) {
            return JSON.parse(listDocTypes)
        }
        return []
    },
    getPostalAdressFromPostalParamnetre: function (postalParametre: BusinessConfigType) {
        return {
            name: postalParametre.configValues.find((v) => v.key === "Sender_Name")?.value,
            additionalInfo: postalParametre.configValues.find((v) => v.key === "Sender_AdditionalInfo")?.value,
            additionalGeographicInfo: postalParametre.configValues.find(
                (v) => v.key === "Sender_AdditionalGeographicInfo"
            )?.value,
            street: postalParametre.configValues.find((v) => v.key === "Sender_Street")?.value,
            postBox: postalParametre.configValues.find((v) => v.key === "Sender_PostBox")?.value,
            zipCode: postalParametre.configValues.find((v) => v.key === "Sender_ZipCode")?.value,
            city: postalParametre.configValues.find((v) => v.key === "Sender_City")?.value,
            country: postalParametre.configValues.find((v) => v.key === "Sender_Country")?.value
        }
    },

    GetNatureListByCode: function (code: string) {
        const listNature = localStorage.getItem(localStorageItemName.listNatures)
        if (listNature !== null) {
            if (code !== "") {
                return JSON.parse(listNature)?.filter((item: any) => {
                    return item.code?.toUpperCase() === code?.toUpperCase()
                })
            } else {
                return JSON.parse(listNature)
            }
        }
        return []
    },
    GetNatureIndexByCode: function (code: string) {
        const listNature = localStorage.getItem(localStorageItemName.listNatures)
        if (listNature !== null) {
            return JSON.parse(listNature).findIndex((item: any) => item.code?.toUpperCase() === code?.toUpperCase())
        }
        return -1
    },
    GetTypeLabbelById: function (id: string) {
        const listType = localStorage.getItem(localStorageItemName.listType)
        if (listType !== null) {
            const index = JSON.parse(listType).findIndex((item: any) => item.id === id)
            if (index !== -1) {
                return JSON.parse(listType)[index]?.name
            }
        }
        return id
    },

    DeleteColView: function (index: number): boolean {
        const listView = localStorage.getItem(localStorageItemName.columnView)
        if (listView) {
            const colList: any[] = JSON.parse(listView)
            const listViewJson = colList
            listViewJson.splice(index, 1)
            localStorage.setItem(localStorageItemName.columnView, JSON.stringify(listViewJson))
            return true
        } else return true
    },
    GetAllColViewComplete: function (): {
        name: string
        cfg: object
        table: string
    }[] {
        const listFunction = localStorage.getItem(localStorageItemName.columnView)
        if (listFunction) {
            const colList: any[] = JSON.parse(listFunction) ? JSON.parse(listFunction) : []
            return colList.filter((item) => item?.tenant === getTenantName())
        } else return []
    },
    ColViewEditSubList: function (newOrderListe: string[]): boolean {
        const listCol = localStorage.getItem(localStorageItemName.columnView)
        if (listCol && newOrderListe) {
            const colList: {name: string; cfg: object}[] = JSON.parse(listCol) ? JSON.parse(listCol) : []
            const subList = newOrderListe.map((item: string) => {
                return colList.find((element: any) => element.name === item)
            })

            const newListe = [...colList.filter((item: any) => !newOrderListe.includes(item.name)), ...subList]
            localStorage.setItem(localStorageItemName.columnView, JSON.stringify(newListe))

            return true
        } else return false
    },
    GetAllColView: function (tableName: string): {name: string; cfg: object; natureCodes: string}[] {
        // Custom Views
        const listFunction = localStorage.getItem(localStorageItemName.columnView)
        if (listFunction) {
            const colList: any[] = JSON.parse(listFunction) ? JSON.parse(listFunction) : []
            return colList?.filter(
                (item: {name: string; cfg: object; table: string; tenant: string; natureCodes: string}) =>
                    item.table === tableName &&
                    item.tenant === JSON.parse(this.GetLocal(localStorageItemName.selectedRole)).tenantName
            )
        } else return []
    },
    CheckNameColView: function (tableName: string, name: string): boolean {
        const listFunction = localStorage.getItem(localStorageItemName.columnView)
        if (listFunction) {
            const colList: any[] = JSON.parse(listFunction) ? JSON.parse(listFunction) : []
            return (
                colList.findIndex(
                    (item: {name: string; cfg: object; table: string}) => item.table === tableName && item.name === name
                ) === -1
            )
        } else return true
    },
    GetIndexColViewByIndex: function (index: string, tableName: string): any {
        const listCol = localStorage.getItem(localStorageItemName.columnView)
        if (listCol) {
            const colList: any[] = JSON.parse(listCol) ? JSON.parse(listCol) : []
            const element = colList?.filter(
                (item: {name: string; cfg: object; table: string}) => item.table === tableName
            )[parseInt(index)]
            return colList.findIndex((item: {name: string; cfg: object; table: string}) => item.name === element.name)
        } else return -1
    },
    GetCheckColViewByIndex: function (index: string, tableName: string): any {
        const listCol = localStorage.getItem(localStorageItemName.columnView)
        if (listCol) {
            const colList: any[] = JSON.parse(listCol) ? JSON.parse(listCol) : []
            const element = colList?.filter(
                (item: {name: string; cfg: object; table: string}) => item.table === tableName
            )[parseInt(index)]
            return element ? element : -1
        } else return -1
    },
    GetCheckColView: function (name: string, tableName: string): number {
        const listCol = localStorage.getItem(localStorageItemName.columnView)
        if (listCol) {
            const colList: any[] = JSON.parse(listCol) ? JSON.parse(listCol) : []
            return colList.findIndex(
                (item: {name: string; cfg: object; table: string}) => item.name === name && item.table === tableName
            )
        } else return -1
    },
    GetFunctionLabbelById: function (id: string) {
        const listFunction = localStorage.getItem("listFunction")
        if (listFunction !== null) {
            const index = JSON.parse(listFunction).findIndex((item: any) => item.id === id)
            if (index !== -1) {
                return JSON.parse(listFunction)[index]?.name
            }
        }
        return id
    },
    checkChorusRead: function (substatus: string) {
        return (
            substatus?.indexOf("RECEIVED_BY_PLATFORM") !== -1 ||
            substatus?.indexOf("SUSPENDED") !== -1 ||
            substatus?.indexOf("APPROVED") !== -1 ||
            substatus?.indexOf("PARTIALLY_APPROVED") !== -1 ||
            substatus?.indexOf("REFUSED") !== -1 ||
            substatus?.indexOf("PAYMENT_RECEIVED") !== -1 ||
            substatus?.indexOf("PAYMENT_SENT") !== -1 ||
            substatus?.indexOf("IN_HAND") !== -1 ||
            substatus?.indexOf("DISPUTE") !== -1
        )
    },
    GetNatureOfDocType: function (docTypeCode: string): any {
        const docTypeListeString = localStorage.getItem(localStorageItemName.listDocumentType)
        if (docTypeListeString !== null) {
            const docTypeListe = JSON.parse(docTypeListeString)
            const code = docTypeListe.find((item: any) => item.code === docTypeCode)?.natureCode
            return this.GetNatureListByCode(code)
        }
        return ""
    },
    GetNatureLabel: function (natureCode: string): string {
        const listNature = localStorage.getItem(localStorageItemName.listNatures)
        if (listNature !== null) {
            return (
                JSON.parse(listNature).find((item: any) => {
                    return item.code?.toUpperCase() === natureCode?.toUpperCase()
                })?.label + ""
            )
        }
        return ""
    },
    GetCycleLabel: function (natureCode: string): string {
        const listNature = localStorage.getItem(localStorageItemName.listNatures)
        if (listNature !== null) {
            return (
                JSON.parse(listNature).find((item: any) => {
                    return item.code?.toUpperCase() === natureCode?.toUpperCase()
                })?.cycle + ""
            )
        }
        return ""
    },
    GetFullLanguage: function (lang: string) {
        switch (lang?.toLowerCase()) {
            case "en":
                return "English"

            case "fr":
                return "Français"
            default:
                return "Français"
        }
    },
    rightPath: function (location: string): boolean {
        return location.indexOf("/nd/") !== -1 || location.indexOf("/da/") !== -1
    },
    goHome: function (): void {
        window.location.href = "/"
    },
    resetLocalStorage: function () {
        const columnView = CommonUtils.GetLocal(localStorageItemName.columnView)
        const idToken = CommonUtils.GetLocal(localStorageItemName.idToken)
        const accessToken = CommonUtils.GetLocal(localStorageItemName.accessToken)
        localStorage.clear()
        localStorage.setItem(localStorageItemName.columnView, columnView)
        localStorage.setItem(localStorageItemName.idToken, idToken)
        localStorage.setItem(localStorageItemName.accessToken, accessToken)
        window.location.href = "/"
    },
    setLocal: (name: string, value: string) => {
        localStorage.setItem(name, value)
    },

    GetLocal: function (name: string): string {
        if (localStorage.getItem(name) === null) {
            return null
        }
        const localValue = localStorage.getItem(name)
        return localValue === null ? "" : localValue
    },
    GetLanguage: function (): string {
        return CommonUtils.GetLocal(localStorageItemName.language) || "FR"
    },
    ToBoolean: function (value: any) {
        return String(value).toLowerCase() === "true" || value === 1
    },
    GetCountryList: function (): string[] {
        const translatedCountriesList: any = []

        countries.forEach((country: any) => {
            switch (this.GetLanguage().toLowerCase()) {
                case "fr":
                    translatedCountriesList.push(country["fr"])
                    break

                case "en":
                    translatedCountriesList.push(country["en"])
                    break
            }
        })

        return translatedCountriesList
    },
    GetFormatedDate: function (date: string): string {
        switch (this.GetLocal(localStorageItemName.language)) {
            case "fr":
                return moment(date)
                    .locale(CommonUtils.GetLocal(localStorageItemName.language))
                    .format("DD/MM/YYYY à HH:mm")
            default:
                return ""
        }
    },
    getValueOfKey: function (liste: KeyValue[], key: string): string {
        const index = liste?.findIndex((item: KeyValue) => item.key === key)
        if (index === -1 || !liste) {
            return ""
        }
        return liste[index].value
    },

    validateEmail: function name(email: string | undefined): boolean {
        return (
            email !== "" &&
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
                email + "".toLowerCase()
            )
        )
    },
    /**
     * Get the default culture based on the browser's language settings.
     */
    GetDefaultCulture: function () {
        if (navigator.language.includes("fr")) {
            return "fr-FR"
        }

        return "en-US"
    },
    /**
     * Returns the current tenant user from localStorage, or null if not found.
     */
    GetTenantUser: function (): TenantUser | null {
        if (typeof window === "undefined") {
            return null
        }

        try {
            const storedUser = this.GetLocal(localStorageItemName.tenantUser)
            if (storedUser) {
                return JSON.parse(storedUser) as TenantUser
            }
        } catch {
            console.error("Failed to parse stored user tenant from localStorage.")
        }

        return null
    },
    /**
     * Resolves the active culture locale.
     */
    GetCulture: function (): string {
        const tenantUser = this.GetTenantUser()

        if (tenantUser) {
            const culture = tenantUser?.culture

            if (culture && culture.includes("-")) {
                return culture
            }
        }

        const language = this.GetLocal(localStorageItemName.language)?.toLowerCase()

        switch (language) {
            case "fr":
                return "fr-FR"
            case "en":
            case "gb":
                return "en-US"
            default:
                return this.GetDefaultCulture()
        }
    },
    /**
     * Indicates whether a metadata type represents a date that should be formatted.
     * Covers the GedFieldDataType DATE type.
     */
    IsDateDataType: function (dataType: string | null | undefined): boolean {
        if (!dataType) {
            return false
        }

        const dataTypes = [GedFieldDataType.Date]
        const dt = dataType.toUpperCase()

        return dataTypes.some((d) => String(d).toUpperCase() === dt)
    },
    /**
     * Indicates whether a metadata type represents a decimal number that
     * should be formatted. Covers the GedFieldDataType DOUBLE and DECIMAL types.
     */
    IsDecimalDataType: function (dataType: string | null | undefined): boolean {
        if (!dataType) {
            return false
        }

        const dataTypes = [GedFieldDataType.Double, GedFieldDataType.Decimal]
        const dt = dataType.toUpperCase()

        return dataTypes.some((d) => String(d).toUpperCase() === dt)
    },
    /**
     * Formats a number according to the tenant's culture.
     */
    FormatNumber: function (value: number | string | null | undefined): string {
        if (value === null || value === undefined) {
            return ""
        }

        if (typeof value === "string" && value.trim() === "") {
            return ""
        }

        const numericValue = typeof value === "number" ? value : Number(value)

        if (Number.isNaN(numericValue)) {
            return String(value)
        }

        const culture = this.GetCulture()
        let formatter = numberFormatCache.get(culture)

        if (!formatter) {
            formatter = new Intl.NumberFormat(culture)
            numberFormatCache.set(culture, formatter)
        }

        return formatter.format(numericValue)
    },
    /**
     * @deprecated
     */
    limitLenght: function (text: string) {
        return this.truncate(text)
    },
    /**
     * @deprecated
     */
    limitStringLength: function (text: string, limit: number) {
        return this.truncate(text, limit)
    },
    /**
     * @deprecated
     */
    limitLenghtCustom: function (text: string, length: number) {
        return this.truncate(text, length)
    },

    truncate: function (text: string, limit: number = 30) {
        if (text?.length > limit) {
            return text.slice(0, limit) + "..."
        } else {
            return text
        }
    },

    getDomain: function (id: string) {
        const listDomainString = localStorage.getItem(localStorageItemName.domainList)
        if (listDomainString) {
            if (id) {
                const listDomain = JSON.parse(listDomainString)
                return listDomain.find((item: any) => item.id === id)
            } else {
                return null
            }
        }
        return null
    },
    equalsIgnoreCase: (value1?: string | null, value2?: string | null): boolean => {
        return (value1 ?? "").toLowerCase() === (value2 ?? "").toLowerCase()
    },
    initEntity: function (
        natureList: any[] | null,
        thirdPartyTypeSystemList: any[] | null,
        contactFunctionSystemList: any[] | null,
        documentTypeList: any[] | null
    ) {
        if (natureList) {
            localStorage.setItem(
                localStorageItemName.listNatures,
                JSON.stringify(
                    natureList.filter((item: Nature) => {
                        return !CHORUS_NATURES.map((c) => c.toUpperCase()).includes(item.code?.toUpperCase())
                    })
                )
            )
        }
        if (thirdPartyTypeSystemList) {
            localStorage.setItem(localStorageItemName.listType, JSON.stringify(thirdPartyTypeSystemList))
        }
        if (contactFunctionSystemList) {
            localStorage.setItem(localStorageItemName.listFunction, JSON.stringify(contactFunctionSystemList))
        }
        if (documentTypeList) {
            localStorage.setItem(localStorageItemName.listDocumentType, JSON.stringify(documentTypeList))
        }
    },
    getSeniority: function (lastOccurrence: string) {
        const today = new Date()
        const lastAction = new Date(lastOccurrence)

        const seniorityDate = Math.ceil(Math.abs(lastAction.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

        if (seniorityDate < 1) {
            return {
                duration: Math.ceil(Math.abs(lastAction.getTime() - today.getTime()) / (1000 * 60 * 60)),
                type: "hours"
            }
        }

        if (seniorityDate < 31) {
            return {duration: seniorityDate, type: "day"}
        }

        if (seniorityDate < 365) {
            return {duration: Math.ceil(seniorityDate / 30), type: "month"}
        }

        return {duration: Math.ceil(seniorityDate / 365), type: "years"}
    },

    removeNullTenants: (environments: Environment[]): Environment[] => {
        return environments.map((environment) => ({
            ...environment, // Conserve les autres propriétés de l'environnement
            domains: environment.domains.map((domain) => ({
                ...domain, // Conserve les autres propriétés du domaine
                tenants: domain.tenants.filter((tenant) => tenant.tenantId !== null) // Filtrer les tenants dont l'ID n'est pas null
            }))
        }))
    },
    groupRoles: (roles: RoleAuth[]): Environment[] => {
        return roles.reduce((result, role) => {
            // Vérifie si l'environnement existe déjà dans le résultat
            let environment = result.find((env) => env.environmentId === role.environmentId)

            if (!environment) {
                // Si l'environnement n'existe pas, on le crée
                environment = {
                    environmentId: role.environmentId,
                    environmentName: role.environmentName,
                    domains: []
                }
                result.push(environment)
            }

            // Vérifie si le domaine existe déjà dans cet environnement
            let domain = environment.domains.find((dom) => dom.domainId === role.domainId)

            if (!domain) {
                // Si le domaine n'existe pas, on le crée
                domain = {
                    domainId: role.domainId,
                    domainName: role.domainName,
                    environmentId: role.environmentId,
                    tenants: [],
                    profile: role.profile,
                    token: role.token
                }
                environment.domains.push(domain)
            } else {
                if (role.profile === DOMAIN_ADMINISTRATOR) {
                    domain.profile = DOMAIN_ADMINISTRATOR
                    domain.token = role.token
                }
            }

            // Vérifie si le tenant existe déjà dans ce domaine
            let tenant = domain.tenants.find((ten) => ten.tenantId === role.tenantId)

            if (!tenant) {
                // Si le tenant n'existe pas, on le crée
                tenant = {
                    tenantId: role.tenantId,
                    tenantName: role.tenantName,
                    domainName: role.domainName,
                    domainId: role.domainId,
                    isEnabled: role.isEnabled,
                    token: role.token
                }
                domain.tenants.push(tenant)
            }
            return result
        }, [])
    }
}

export default CommonUtils
